/*************************************************************************
 *
 * c20g CONFIDENTIAL
 * __________________
 *
 *  [2007] - [2021] Counterpoint Consulting, Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Counterpoint Consulting Incorporated.
 * The intellectual and technical concepts contained
 * herein are proprietary to Counterpoint Consulting Incorporated
 * and its suppliers and may be covered by U.S. and Foreign Patents,
 * patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Counterpoint Consulting Incorporated.
 */

import { View } from 'backbone.marionette';
import Ladda from 'ladda';
import Radio from 'backbone.radio';

import showErrorMessage from '@/util/showErrorMessage';
import { userHasRole } from '@/util/RoleUtils';
import lazyLoadView from '@/util/lazyLoadView';

import IntDashboardSidebarNavView from '../../../internal-dashboard/views/IntDashboardSidebarNavView';
import loadSearchFields from '../../../login/loaders/SearchFieldsViewLoader';
import OrderPackageDashboardGridView from '../../views/OrderPackageDashboardGridView';
import GetAllOrderFormTypes from '../../../common/services/GetAllOrderFormTypes';
import GetAllOrderPackageStatuses from '../../services/GetAllOrderPackageStatuses';
import SearchOrderPackages from '../../services/SearchOrderPackages';
import CreateOrderPackage from '../../services/CreateOrderPackage';

import tmplOrderPackageDashboardPage from './OrderPackageDashboardPage.hbs';

const OrderPackageDashboardPage = View.extend({
  template: tmplOrderPackageDashboardPage,

  ui: {
    sideBar: '.sidebar-nav-container',
    searchFieldsView: '.search-fields-view',
    orderPackagesGrid: '#orderPackagesGrid',
    btnCreatOrderPackage: '.btn-create-order-package'
  },

  regions: {
    sideBar: '@ui.sideBar',
    searchFieldsView: '@ui.searchFieldsView',
    orderPackagesGrid: '@ui.orderPackagesGrid'
  },

  events: {
    'click @ui.btnCreatOrderPackage': 'onClickCreateOrderPackage'
  },

  childViewEvents: {
    'search:response': 'onSearchResponse'
  },

  templateContext() {
    return {
      isCRS: userHasRole('CRS')
    };
  },

  onRender() {
    this.showChildView('sideBar', new IntDashboardSidebarNavView({ mode: 'order-packages' }));

    const searchFields = [
      {
        id: 'order-package-id-search-field',
        name: 'orderPackageID',
        class: 'InputSearchField',
        placeholder: 'Order Package ID',
        dataParsleyType: 'integer'
      },
      {
        id: 'command-name-search-field',
        name: 'commandName',
        class: 'InputSearchField',
        placeholder: 'Command Name'
      },
      {
        id: 'status-search-field',
        name: 'statusID',
        class: 'SelectSearchField',
        placeholder: 'Status',
        lookupService: GetAllOrderPackageStatuses
      },
      {
        id: 'type-search-field',
        name: 'typeID',
        class: 'SelectSearchField',
        placeholder: 'Type',
        lookupService: GetAllOrderFormTypes,
        lookupMapping: (type) => {
          const { formName, id } = type;
          return { optionText: formName, id };
        }
      },
      {
        id: 'date-created-start-search-field',
        name: 'createdOnStart',
        class: 'DateSearchField',
        placeholder: 'Created On Start'
      },
      {
        id: 'date-created-end-search-field',
        name: 'createdOnEnd',
        class: 'DateSearchField',
        placeholder: 'Created On End'
      },
      {
        id: 'pop-start-date-start-search-field',
        name: 'popStartDateStart',
        class: 'DateSearchField',
        placeholder: 'POP Start Date Start'
      },
      {
        id: 'pop-start-date-end-search-field',
        name: 'popStartDateEnd',
        class: 'DateSearchField',
        placeholder: 'POP Start Date End'
      }
    ];

    this.showChildView(
      'searchFieldsView',
      lazyLoadView(() => loadSearchFields(searchFields, SearchOrderPackages))
    );

    this.orderPackagesGrid = new OrderPackageDashboardGridView({});

    this.showChildView('orderPackagesGrid', this.orderPackagesGrid);
  },

  onSearchResponse(response) {
    this.$('.result-count').empty().append(`<span><b>Total Result Count: ${response.totalCount}</b></span>`);
    this.orderPackagesGrid.collection.fullCollection.reset(response.orderPackages);
  },

  showOrderPackageCreationError() {
    showErrorMessage({
      errorCode: 'orderPackageCreationError',
      errorTitle: 'Creation Error',
      errorMessage:
        'An error occurred while creating the order Package. Try again or contact an administrator if your issue persists.'
    });
  },

  async onClickCreateOrderPackage(e) {
    e.preventDefault();
    const spinner = Ladda.create(this.ui.btnCreatOrderPackage[0]);
    spinner.start();

    try {
      const newOrderPackage = await CreateOrderPackage();
      Radio.channel('navigate').request('show:int:order-package', newOrderPackage.id);
    } catch (err) {
      this.showOrderPackageCreationError();
    } finally {
      spinner.stop();
    }
  }
});

export default OrderPackageDashboardPage;
